import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'

type ApiErrorResponse = {
    error?: unknown
    message?: unknown
    details?: unknown
}

function stringValue(value: unknown) {
    return typeof value === 'string' && value.trim().length > 0 ? value : null
}

function detailsMessage(details: unknown) {
    if (typeof details === 'string' && details.trim().length > 0) {
        return details
    }

    if (Array.isArray(details)) {
        const messages = details
            .map((detail) => {
                if (typeof detail === 'string') return detail
                if (detail && typeof detail === 'object' && 'message' in detail) {
                    return stringValue((detail as { message?: unknown }).message)
                }
                return null
            })
            .filter(Boolean)

        return messages.length > 0 ? messages.join('\n') : null
    }

    if (details && typeof details === 'object') {
        const detailBody = details as ApiErrorResponse
        return stringValue(detailBody.error) ?? stringValue(detailBody.message)
    }

    return null
}

export async function readTradeOfferError(response: Response, fallback: string) {
    try {
        const payload = await response.json() as ApiErrorResponse
        return stringValue(payload.error)
            ?? stringValue(payload.message)
            ?? detailsMessage(payload.details)
            ?? fallback
    } catch {
        return fallback
    }
}

export function useTradeOffers(teamId?: number) {
    const { toast } = useToast()
    const queryClient = useQueryClient()

    // Include teamId in queryKey when fetching for a specific team (admin viewing another team)
    const queryKey = teamId ? ['tradeOffers', teamId] : ['tradeOffers']

    const { data: tradeOffers, isLoading } = useQuery({
        queryKey,
        queryFn: async () => {
            const url = teamId
                ? `/api/trade-offers?teamId=${teamId}`
                : '/api/trade-offers'
            const response = await fetch(url)
            if (!response.ok) {
                throw new Error('Failed to fetch trade offers')
            }
            return response.json()
        }
    })

    const { mutate: acceptTradeOffer, isPending: isAccepting } = useMutation({
        mutationFn: async ({ offerId, overrideDate }: { offerId: number, overrideDate: string }) => {
            const response = await fetch(`/api/trade-offers/${offerId}/accept`, {
                method: 'PUT',
                body: JSON.stringify({ overrideDate })
            })
            if (!response.ok) {
                throw new Error(await readTradeOfferError(response, 'Failed to accept trade offer'))
            }
            return response.json()
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tradeOffers'] })
            toast({
                title: 'Trade offer accepted',
                description: 'The trade offer has been accepted successfully.'
            })
        },
        onError: (error) => {
            toast({
                title: 'Error accepting trade offer',
                description: error instanceof Error ? error.message : 'An unexpected error occurred',
                variant: 'destructive'
            })
        }
    })

    const { mutate: rejectTradeOffer, isPending: isRejecting } = useMutation({
        mutationFn: async (offerId: number) => {
            const response = await fetch(`/api/trade-offers/${offerId}/reject`, {
                method: 'PUT'
            })
            if (!response.ok) {
                throw new Error(await readTradeOfferError(response, 'Failed to reject trade offer'))
            }
            return response.json()
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tradeOffers'] })
            toast({
                title: 'Trade offer rejected',
                description: 'The trade offer has been rejected successfully.'
            })
        },
        onError: (error) => {
            toast({
                title: 'Error rejecting trade offer',
                description: error instanceof Error ? error.message : 'An unexpected error occurred',
                variant: 'destructive'
            })
        }
    })

    const { mutate: withdrawTradeOffer, isPending: isWithdrawing } = useMutation({
        mutationFn: async (offerId: number) => {
            const response = await fetch(`/api/trade-offers/${offerId}/withdraw`, {
                method: 'PUT'
            })
            if (!response.ok) {
                throw new Error(await readTradeOfferError(response, 'Failed to withdraw trade offer'))
            }
            return response.json()
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tradeOffers'] })
            toast({
                title: 'Trade offer withdrawn',
                description: 'The trade offer has been withdrawn successfully.'
            })
        },
        onError: (error) => {
            toast({
                title: 'Error withdrawing trade offer',
                description: error instanceof Error ? error.message : 'An unexpected error occurred',
                variant: 'destructive'
            })
        }
    })

    return {
        tradeOffers,
        isLoading,
        acceptTradeOffer,
        rejectTradeOffer,
        withdrawTradeOffer,
        isAccepting,
        isRejecting,
        isWithdrawing
    }
} 
